import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { setPlayerModalUrlParam, usePlayerModalStateValue, type PlayerModalRefType } from "@/hooks/playerModal";
import { InfoIcon, ListIcon, HistoryIcon, GavelIcon, ExternalLinkIcon } from "lucide-react";
import PlayerInfoTab from "./PlayerInfoTab";
import { useEffect, useRef, useState } from "react";
import PlayerIdsTab from "./PlayerIdsTab";
import PlayerHistoryTab from "./PlayerHistoryTab";
import PlayerBanTab from "./PlayerBanTab";
import GenericSpinner from "@/components/GenericSpinner";
import { cn } from "@/lib/utils";
import { ApiTimeout, useBackendApi } from "@/hooks/fetch";
import { PlayerModalResp, PlayerModalSuccess } from "@shared/playerApiTypes";
import PlayerModalFooter from "./PlayerModalFooter";
import { ModalContent, ModalTabMessage, ModalTabsList, ModalTabWrapper, type ModalTabInfo } from "@/components/modal-tabs";
import { playerlistAtom } from "@/hooks/playerlist";
import { useAtomValue } from "jotai";
import { usePlayerDossier } from '@/hooks/PlayerDossierProvider';
import { playerModalDossierSeed } from './playerModalDossierModel';


const modalTabs: ModalTabInfo[] = [
    {
        title: 'Info',
        icon: <InfoIcon className="mr-2 h-5 w-5 hidden xs:block" />,
    },
    {
        title: 'History',
        icon: <HistoryIcon className="mr-2 h-5 w-5 hidden xs:block" />,
    },
    {
        title: 'IDs',
        icon: <ListIcon className="mr-2 h-5 w-5 hidden xs:block" />,
    },
    {
        title: 'Ban',
        icon: <GavelIcon className="mr-2 h-5 w-5 hidden xs:block" />,
        className: 'hover:bg-destructive hover:text-destructive-foreground',
    }
];

type LoadedModalData = {
    playerRefKey: string;
    data: PlayerModalSuccess;
    tsFetch: number;
};

type ModalError = {
    playerRefKey: string;
    message: string;
};

const getPlayerRefKey = (playerRef: PlayerModalRefType | undefined) => {
    if (!playerRef) return '';
    return 'license' in playerRef
        ? `license:${playerRef.license}`
        : `live:${playerRef.mutex}:${playerRef.netid}`;
};


export default function PlayerModal() {
    const { isModalOpen, closeModal, playerRef } = usePlayerModalStateValue();
    const playerlist = useAtomValue(playerlistAtom);
    const [selectedTab, setSelectedTab] = useState(modalTabs[0].title);
    const [currRefreshKey, setCurrRefreshKey] = useState(0);
    const [loadedModal, setLoadedModal] = useState<LoadedModalData | undefined>(undefined);
    const [queryError, setQueryError] = useState<ModalError | undefined>(undefined);
    const requestSequence = useRef(0);
    const { openDossier } = usePlayerDossier();
    const currentPlayerRefKey = getPlayerRefKey(playerRef);
    const modalData = loadedModal?.playerRefKey === currentPlayerRefKey
        ? loadedModal.data
        : undefined;
    const modalError = queryError?.playerRefKey === currentPlayerRefKey
        ? queryError.message
        : '';
    const dossierSeed = playerModalDossierSeed(modalData?.player);
    const playerQueryApi = useBackendApi<PlayerModalResp>({
        method: 'GET',
        path: `/player`,
        abortOnUnmount: true,
    });

    //Helper for tabs to be able to refresh the modal data
    const refreshModalData = () => {
        setCurrRefreshKey((current) => current + 1);
    };

    //Querying player data when reference is available
    useEffect(() => {
        const sequence = ++requestSequence.current;
        if (!playerRef) {
            setLoadedModal(undefined);
            setQueryError(undefined);
            return;
        }
        const requestedPlayerRef = playerRef;
        const requestedPlayerRefKey = getPlayerRefKey(requestedPlayerRef);
        setLoadedModal(undefined);
        setQueryError(undefined);
        playerQueryApi({
            queryParams: requestedPlayerRef,
            timeout: ApiTimeout.REALLY_REALLY_LONG,
            success: (resp) => {
                if (requestSequence.current !== sequence) return;
                if ('error' in resp) {
                    setQueryError({ playerRefKey: requestedPlayerRefKey, message: resp.error });
                } else {
                    setLoadedModal({
                        playerRefKey: requestedPlayerRefKey,
                        data: resp,
                        tsFetch: Math.round(Date.now() / 1000),
                    });
                    //Update the ref param to use a license, if possible
                    if (!('license' in requestedPlayerRef) && resp.player.license) {
                        setPlayerModalUrlParam(resp.player.license)
                    }
                }
            },
            error: (error) => {
                if (requestSequence.current !== sequence) return;
                setQueryError({ playerRefKey: requestedPlayerRefKey, message: error });
            },
        });
        return () => {
            if (requestSequence.current === sequence) requestSequence.current += 1;
        };
    }, [playerRef, currRefreshKey]);

    //Resetting selected tab when modal is closed
    useEffect(() => {
        if (!isModalOpen) {
            setTimeout(() => {
                setSelectedTab(modalTabs[0].title);
            }, 200);
        }
    }, [isModalOpen]);

    const handleOpenClose = (newOpenState: boolean) => {
        if (isModalOpen && !newOpenState) {
            closeModal();
        }
    };

    const openFcDossier = () => {
        if (!dossierSeed) return;
        closeModal();
        openDossier(dossierSeed);
    };

    //Move to tab up or down
    const handleTabButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const currentIndex = modalTabs.findIndex((tab) => tab.title === selectedTab);
            const nextIndex = e.key === 'ArrowUp' ? currentIndex - 1 : currentIndex + 1;
            const nextTab = modalTabs[nextIndex];
            if (nextTab) {
                setSelectedTab(nextTab.title);
                const nextButton = document.getElementById(`player-modal-tab-${nextTab.title}`);
                if (nextButton) {
                    nextButton.focus();
                }
            }
        }
    }

    let pageTitle: JSX.Element;
    if (modalData) {
        if (modalData.player.netid) {
            const livePlayer = playerlist.find((player) => player.netid === modalData.player.netid);
            const permanentId = livePlayer ? livePlayer.permanentId : modalData.player.permanentId;
            pageTitle = <>
                <span className="text-success-inline font-mono mr-2">[{permanentId ?? '–'}]</span>
                {modalData.player.displayName}
            </>;
        } else {
            pageTitle = <>
                <span className="text-destructive-inline font-mono mr-2">[OFF]</span>
                {modalData.player.displayName}
            </>;
        }
    } else if (modalError) {
        pageTitle = <span className="text-destructive-inline">Error!</span>;
    } else {
        pageTitle = <span className="text-muted-foreground italic">Loading...</span>;
    }

    return (
        <Dialog open={isModalOpen} onOpenChange={handleOpenClose}>
            <DialogContent className="max-w-2xl h-full sm:h-auto max-h-full p-0 gap-1 sm:gap-4 flex flex-col">
                <DialogHeader className="px-4 py-3 border-b">
                    <div className="flex items-center gap-3 pr-6">
                        <DialogTitle className="min-w-0 flex-1 tracking-wide line-clamp-1 leading-7 break-all">
                            {pageTitle}
                        </DialogTitle>
                        {dossierSeed && (
                            <Button size="xs" variant="outline" onClick={openFcDossier}>
                                <ExternalLinkIcon className="mr-1 size-4" />FC-Akte
                            </Button>
                        )}
                    </div>
                </DialogHeader>

                {modalData && !modalData.player.moderation.available && (
                    <div className="mx-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
                        <span className="min-w-64 flex-1 text-warning-inline">
                            {modalData.player.moderation.message ?? 'Die FC-Moderationsakte ist hier nicht eindeutig verfügbar.'}
                        </span>
                    </div>
                )}

                <ModalContent>
                    <ModalTabsList>
                        {modalTabs.map((tab) => (
                            <Button
                                id={`player-modal-tab-${tab.title}`}
                                key={tab.title}
                                variant={selectedTab === tab.title ? "secondary" : "ghost"}
                                className={cn(
                                    'w-full tracking-wider justify-center md:justify-start',
                                    'h-7 rounded-sm px-2 text-sm',
                                    'md:h-10 md:text-base',
                                    tab.className,
                                )}
                                onClick={() => setSelectedTab(tab.title)}
                                onKeyDown={handleTabButtonKeyDown}
                            >
                                {tab.icon} {tab.title}
                            </Button>
                        ))}
                    </ModalTabsList>
                    
                    <ModalTabWrapper className="max-h-[calc(100vh-3.125rem-4rem-5rem)]">
                        {!modalData ? (
                            <ModalTabMessage>
                                {modalError ? (
                                    <span className="text-destructive-inline">Error: {modalError}</span>
                                ) : (
                                    <GenericSpinner msg="Loading..." />
                                )}
                            </ModalTabMessage>
                        ) : (
                            <>
                                {selectedTab === 'Info' && <PlayerInfoTab
                                    player={modalData.player}
                                    playerRef={playerRef!}
                                    serverTime={modalData.serverTime}
                                    tsFetch={loadedModal!.tsFetch}
                                    setSelectedTab={setSelectedTab}
                                    refreshModalData={refreshModalData}
                                    moderation={modalData.player.moderation}
                                />}
                                {selectedTab === 'History' && <PlayerHistoryTab
                                    actionHistory={modalData.player.actionHistory}
                                    serverTime={modalData.serverTime}
                                    moderation={modalData.player.moderation}
                                />}
                                {selectedTab === 'IDs' && <PlayerIdsTab
                                    player={modalData.player}
                                    playerRef={playerRef!}
                                    refreshModalData={refreshModalData}
                                />}
                                {selectedTab === 'Ban' && <PlayerBanTab
                                    banTemplates={modalData.banTemplates}
                                    playerRef={playerRef!}
                                    moderation={modalData.player.moderation}
                                />}
                            </>
                        )}
                    </ModalTabWrapper>
                </ModalContent>
                <PlayerModalFooter
                    playerRef={playerRef!}
                    player={modalData?.player}
                    moderation={modalData?.player.moderation}
                />
            </DialogContent>
        </Dialog>
    );
}
